import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import logger from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Clock, ChefHat, CheckCircle2, RefreshCw, Timer, Volume2, VolumeX } from "lucide-react";
import { ORDER_STATUS_LABEL } from "@/lib/ordering";
import { PlatterDetails } from "@/components/PlatterDetails";

const COLUMNS = [
  { key: "pending", label: "New", icon: Clock, next: "preparing", nextLabel: "Start Preparing", tint: "amber" },
  { key: "preparing", label: "In progress", icon: ChefHat, next: "ready", nextLabel: "Mark Ready", tint: "blue" },
  { key: "ready", label: "Ready", icon: CheckCircle2, next: "completed", nextLabel: "Hand Off", tint: "emerald" },
];

const TINT = {
  amber: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", btn: "bg-amber-500 hover:bg-amber-600" },
  blue:  { bg: "bg-blue-50",  border: "border-blue-200",  text: "text-blue-700",  btn: "bg-blue-500 hover:bg-blue-600" },
  emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", btn: "bg-emerald-600 hover:bg-emerald-700" },
};

const STATUS_LABEL = ORDER_STATUS_LABEL;

function formatElapsed(iso) {
  const secs = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}m ${s.toString().padStart(2, "0")}s`;
}

export default function Kitchen() {
  const { sid } = useParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("sandar_kitchen_sound") !== "off");
  const audioCtxRef = useRef(null);
  const knownOrderIdsRef = useRef(null); // null = first load, don't chime
  const squareSnapshotRef = useRef(null); // {orderId: {status, square_synced_at}} — detects POS-driven changes
  const soundOnRef = useRef(soundOn);

  useEffect(() => {
    soundOnRef.current = soundOn;
    localStorage.setItem("sandar_kitchen_sound", soundOn ? "on" : "off");
  }, [soundOn]);

  // Soft chime: two-note bell using Web Audio API
  const playChime = useCallback(() => {
    if (!soundOnRef.current) return;
    try {
      if (!audioCtxRef.current) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;
        audioCtxRef.current = new AC();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const now = ctx.currentTime;
      const notes = [
        { freq: 987.77, start: 0.00, dur: 0.55 },  // B5
        { freq: 1318.5, start: 0.14, dur: 0.75 },  // E6
      ];
      for (const n of notes) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(n.freq, now + n.start);
        gain.gain.setValueAtTime(0.0001, now + n.start);
        gain.gain.exponentialRampToValueAtTime(0.18, now + n.start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + n.start + n.dur);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now + n.start);
        osc.stop(now + n.start + n.dur + 0.05);
      }
    } catch (err) { logger.debug("Kitchen: chime playback failed", err); }
  }, []);

  // Unlock audio on first user gesture anywhere on the page
  useEffect(() => {
    const unlock = () => {
      try {
        if (!audioCtxRef.current) {
          const AC = window.AudioContext || window.webkitAudioContext;
          if (AC) audioCtxRef.current = new AC();
        }
        audioCtxRef.current?.resume?.();
      } catch (err) { logger.debug("Kitchen: audio unlock failed", err); }
    };
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  const load = useCallback(async () => {
    try {
      const url = sid ? `/orders?store_id=${sid}` : "/orders";
      const { data } = await api.get(url);
      // Detect new "pending" orders since last poll to trigger chime
      const activeIds = new Set(data.filter((o) => o.status === "pending").map((o) => o.id));
      if (knownOrderIdsRef.current !== null) {
        const fresh = [...activeIds].filter((id) => !knownOrderIdsRef.current.has(id));
        if (fresh.length > 0) playChime();
      }
      knownOrderIdsRef.current = activeIds;

      // Detect POS-driven status changes: an order whose square_synced_at advanced since our last snapshot
      // means Square (POS/KDS) just pushed the change. Toast it so staff know who moved the ticket.
      const prev = squareSnapshotRef.current;
      if (prev) {
        for (const o of data) {
          const was = prev[o.id];
          if (!was || !o.square_synced_at) continue;
          if (o.square_synced_at !== was.square_synced_at && o.status !== was.status) {
            toast.info(`${o.order_number} → ${STATUS_LABEL[o.status] || o.status}`, {
              description: "Updated from Square POS",
              duration: 6000,
            });
            playChime();
          }
        }
      }
      squareSnapshotRef.current = Object.fromEntries(data.map((o) => [o.id, { status: o.status, square_synced_at: o.square_synced_at || null }]));

      setOrders(data);
      setRefreshedAt(new Date());
    } catch (err) {
      logger.error("Kitchen: failed to refresh orders", err);
    } finally { setLoading(false); }
  }, [playChime, sid]);

  useEffect(() => {
    load();
    const poll = setInterval(load, 5000);
    const timer = setInterval(() => setTick((t) => t + 1), 1000);
    return () => { clearInterval(poll); clearInterval(timer); };
  }, [load]);

  const grouped = useMemo(() => {
    const by = { pending: [], preparing: [], ready: [] };
    for (const o of orders) if (by[o.status]) by[o.status].push(o);
    // oldest first per column so kitchen sees FIFO
    for (const k of Object.keys(by)) by[k].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    return by;
  }, [orders, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const advance = async (o, next) => {
    // Optimistic update
    setOrders((prev) => prev.map((x) => x.id === o.id ? { ...x, status: next } : x));
    try {
      await api.patch(`/orders/${o.id}/status`, { status: next });
      toast.success(`${o.order_number} → ${next}`);
    } catch (err) {
      logger.error("Kitchen: advance failed", err);
      toast.error("Failed to update");
      load();
    }
  };

  const cancel = async (o) => {
    if (!window.confirm(`Cancel order ${o.order_number}?`)) return;
    setOrders((prev) => prev.map((x) => x.id === o.id ? { ...x, status: "cancelled" } : x));
    try { await api.patch(`/orders/${o.id}/status`, { status: "cancelled" }); toast.success("Canceled"); }
    catch (err) { logger.error("Kitchen: cancel failed", err); toast.error("Failed"); load(); }
  };

  const totalActive = grouped.pending.length + grouped.preparing.length + grouped.ready.length;

  return (
    <div className="h-screen flex flex-col bg-neutral-100 overflow-hidden">
      <div className="px-8 py-5 bg-white border-b border-neutral-200 flex items-center justify-between shrink-0">
        <div>
          <p className="eyebrow">Kitchen Display</p>
          <h1 className="font-display font-bold text-2xl tracking-tight" data-testid="kitchen-title">Live Orders <span className="text-neutral-400 font-normal ml-2">({totalActive})</span></h1>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-xs text-neutral-500 flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"/>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"/>
            </span>
            Live · auto-refresh 5s
            {refreshedAt && <span className="ml-1 font-mono">{refreshedAt.toLocaleTimeString()}</span>}
          </div>
          <Button
            variant="outline" size="sm"
            onClick={() => { setSoundOn((s) => { if (!s) { setTimeout(playChime, 100); } return !s; }); }}
            className="rounded-lg"
            title={soundOn ? "Mute new-order chime" : "Turn on chime"}
            data-testid="kitchen-sound-toggle"
          >
            {soundOn ? <Volume2 className="w-4 h-4 mr-1"/> : <VolumeX className="w-4 h-4 mr-1"/>}
            {soundOn ? "Sound on" : "Muted"}
          </Button>
          <Button variant="outline" size="sm" onClick={load} className="rounded-lg" data-testid="kitchen-refresh"><RefreshCw className="w-4 h-4 mr-1"/>Refresh</Button>
        </div>
      </div>

      <div className="flex-1 overflow-hidden grid grid-cols-1 md:grid-cols-3 gap-4 p-4">
        {COLUMNS.map((col) => {
          const list = grouped[col.key];
          const tint = TINT[col.tint];
          const Icon = col.icon;
          return (
            <div key={col.key} className="flex flex-col overflow-hidden bg-white rounded-2xl border border-neutral-200/70" data-testid={`kitchen-col-${col.key}`}>
              <div className={`px-5 py-4 border-b ${tint.border} ${tint.bg} flex items-center justify-between`}>
                <div className={`flex items-center gap-2 ${tint.text}`}>
                  <Icon className="w-5 h-5"/>
                  <h2 className="font-display font-bold text-lg">{col.label}</h2>
                </div>
                <span className={`text-sm font-bold ${tint.text} px-2.5 py-0.5 rounded-full bg-white`} data-testid={`kitchen-count-${col.key}`}>{list.length}</span>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {loading ? (
                  <div className="text-sm text-neutral-400">Loading…</div>
                ) : list.length === 0 ? (
                  <div className="text-sm text-neutral-400 py-10 text-center">All caught up.</div>
                ) : list.map((o) => {
                  const elapsed = formatElapsed(o.created_at);
                  const secs = Math.floor((Date.now() - new Date(o.created_at).getTime()) / 1000);
                  const urgent = col.key === "pending" && secs > 300; // 5min
                  return (
                    <div
                      key={o.id}
                      className={`rounded-xl border-2 p-4 ${urgent ? "border-red-400 bg-red-50" : "border-neutral-200 bg-white"} transition-colors`}
                      data-testid={`kitchen-order-${o.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-mono text-xs text-neutral-500">{o.order_number}</div>
                          <div className="font-display font-bold text-lg mt-0.5 truncate">{o.customer_name}</div>
                        </div>
                        <div className={`text-xs font-mono flex items-center gap-1 shrink-0 ${urgent ? "text-red-600 font-bold" : "text-neutral-500"}`}>
                          <Timer className="w-3 h-3"/> {elapsed}
                        </div>
                      </div>
                      <div className="mt-2 flex items-center gap-2 text-[11px] uppercase tracking-wider">
                        <span className={`px-2 py-0.5 rounded-full ${o.fulfillment === "delivery" ? "bg-purple-100 text-purple-700" : "bg-neutral-100 text-neutral-600"}`}>{o.fulfillment}</span>
                        {o.location && <span className="text-neutral-500 truncate">· {o.location}</span>}
                      </div>
                      {(o.pickup_date || o.pickup_time) && (
                        <div
                          className="mt-2 border-l-2 border-[hsl(var(--primary))] bg-orange-50 px-2 py-1 text-xs font-semibold text-neutral-700"
                          data-testid={`kitchen-pickup-schedule-${o.id}`}
                        >
                          {o.pickup_date ? `Preorder for ${o.pickup_date}` : "Pickup today"}
                          {o.pickup_time ? ` · ${o.pickup_time}` : ""}
                        </div>
                      )}
                      <ul className="mt-3 space-y-1 text-sm">
                        {o.items.map((i, idx) => (
                          <li key={`${o.id}-${i.item_id}-${idx}`} className="block">
                            <div className="flex justify-between gap-3">
                              <span className="min-w-0"><span className="font-bold text-[hsl(var(--primary))]">{i.quantity}×</span> {i.name}</span>
                            </div>
                            <PlatterDetails platter={i.platter} compact />
                          </li>
                        ))}
                      </ul>
                      {o.notes && (
                        <div className="mt-3 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-md p-2">
                          <span className="font-semibold">Note:</span> {o.notes}
                        </div>
                      )}
                      <div className="mt-4 flex gap-2">
                        <Button
                          onClick={() => advance(o, col.next)}
                          className={`flex-1 rounded-lg text-white ${tint.btn}`}
                          data-testid={`kitchen-advance-${o.id}`}
                        >
                          {col.nextLabel}
                        </Button>
                        {col.key === "pending" && (
                          <Button
                            onClick={() => cancel(o)}
                            variant="outline"
                            className="rounded-lg text-red-600 hover:text-red-700 border-red-200"
                            data-testid={`kitchen-cancel-${o.id}`}
                          >
                            Cancel
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
